/**
 * resizeMetrics — did the author hit the handle they were aiming at?
 *
 * We enlarged the handles and gave corners priority on the theory that the
 * reported "1 in 2 misses" was a target-acquisition problem. That's a
 * hypothesis, and shipping it without a way to see whether it worked would
 * leave us guessing again next time.
 *
 * Two signals, both cheap and content-free:
 *
 *   NEAR-MISS — the pointer went down on an EDGE handle while a corner was
 *   closer to the press point than the edge's own midline. That is the
 *   signature of aiming at a corner and catching the edge. It needs no
 *   intent-guessing: it compares where the press landed against where the
 *   corner was.
 *
 *   REGRET — an undo within a few seconds of a resize commit. Not proof (a
 *   deliberate try-then-revert looks identical), but it moves together with
 *   mis-acquisition and is the cheapest proxy we have for "that wasn't what I
 *   meant".
 *
 * Nothing here records page content, text, ids that identify a site, or
 * pointer paths — only which handle, how far off, and elapsed milliseconds.
 */

export type HandleId = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export const isCornerHandle = (h: HandleId): boolean => h.length === 2;

/** The four corner points of a rect, in handle order. */
export function cornerPoints(r: Rect): ReadonlyArray<[HandleId, Point]> {
  const right = r.left + r.width;
  const bottom = r.top + r.height;
  return [
    ["nw", { x: r.left, y: r.top }],
    ["ne", { x: right, y: r.top }],
    ["sw", { x: r.left, y: bottom }],
    ["se", { x: right, y: bottom }],
  ];
}

const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Nearest corner to a point, with its distance in px. */
export function nearestCorner(
  at: Point,
  rect: Rect,
): { handle: HandleId; distance: number } {
  let best: { handle: HandleId; distance: number } | null = null;
  for (const [handle, p] of cornerPoints(rect)) {
    const distance = dist(at, p);
    if (!best || distance < best.distance) best = { handle, distance };
  }
  // cornerPoints always yields four entries, so `best` is set.
  return best as { handle: HandleId; distance: number };
}

export interface Acquisition {
  /** The handle that actually received the press. */
  readonly handle: HandleId;
  /** Closest corner to the press point. */
  readonly nearestCorner: HandleId;
  /** Distance from the press to that corner, px. */
  readonly cornerDistance: number;
  /**
   * An edge was grabbed while a corner was within reach — the shape of the
   * reported bug. Corner presses are never near-misses.
   */
  readonly nearMiss: boolean;
}

/**
 * How close to a corner an EDGE press has to land before we call it a likely
 * mis-aim. Generous on purpose: this counts suspicion, not proof, and a
 * too-tight threshold would under-report exactly the thing we're watching.
 */
export const NEAR_MISS_RADIUS_PX = 32;

export function classifyAcquisition(
  handle: HandleId,
  at: Point,
  rect: Rect,
  radiusPx: number = NEAR_MISS_RADIUS_PX,
): Acquisition {
  const near = nearestCorner(at, rect);
  return {
    handle,
    nearestCorner: near.handle,
    cornerDistance: near.distance,
    nearMiss: !isCornerHandle(handle) && near.distance <= radiusPx,
  };
}

// ── regret window ───────────────────────────────────────────────────────────

/** Undo landing within this long after a resize counts as a regret signal. */
export const REGRET_WINDOW_MS = 3000;

export interface RegretTracker {
  /** Call when a resize commits. */
  readonly resized: (atMs: number) => void;
  /** Call on undo; true when it lands inside the window (and consumes it). */
  readonly undone: (atMs: number) => boolean;
}

export function createRegretTracker(
  windowMs: number = REGRET_WINDOW_MS,
): RegretTracker {
  let lastResizeAt: number | null = null;
  return {
    resized(atMs) {
      lastResizeAt = atMs;
    },
    undone(atMs) {
      if (lastResizeAt === null) return false;
      const within = atMs - lastResizeAt <= windowMs && atMs >= lastResizeAt;
      // Consume either way: one resize can only be regretted once, and a late
      // undo must not make the NEXT undo look like regret.
      lastResizeAt = null;
      return within;
    },
  };
}

// ── sink ────────────────────────────────────────────────────────────────────

/** How the size was changed. `drag` is the handle; the rest need no aim. */
export type ResizeVerb = "drag" | "preset" | "numeric" | "keyboard";

/** Coarse = touch. Without this the coarse-vs-fine comparison the whole
 *  touch argument rests on simply cannot be computed. */
export type PointerKind = "coarse" | "fine";

export type ResizeMetricEvent =
  | ({
      readonly type: "acquire";
      readonly pointerKind: PointerKind;
    } & Acquisition)
  | {
      readonly type: "resize";
      readonly verb: ResizeVerb;
      readonly pointerKind: PointerKind;
    }
  | { readonly type: "regret"; readonly sinceResizeMs: number };

export type MetricsSink = (event: ResizeMetricEvent) => void;

/** Discards everything. The default: measurement must never be a side effect
 *  a user did not ask for, and there is no backend to send to. */
export const noopSink: MetricsSink = () => {};

/**
 * Running totals, so the numbers are readable without a backend: the question
 * "is the near-miss rate falling?" is answerable from one object.
 */
export interface ResizeMetricsSummary {
  readonly acquisitions: number;
  readonly nearMisses: number;
  readonly regrets: number;
  /** 0..1; 0 when nothing has been acquired yet. */
  readonly nearMissRate: number;
  /** Split by input, because that is the comparison that matters. */
  readonly byPointer: Readonly<
    Record<PointerKind, { acquisitions: number; nearMisses: number }>
  >;
  /** Which path actually got used. If the aim-free verbs dominate, the
   *  handle affordance is still wrong however big the target is. */
  readonly byVerb: Readonly<Record<ResizeVerb, number>>;
}

export function createCountingSink(initial?: ResizeMetricsSummary): {
  readonly sink: MetricsSink;
  readonly summary: () => ResizeMetricsSummary;
} {
  let acquisitions = initial?.acquisitions ?? 0;
  let nearMisses = initial?.nearMisses ?? 0;
  let regrets = initial?.regrets ?? 0;
  const byPointer: Record<
    PointerKind,
    { acquisitions: number; nearMisses: number }
  > = {
    coarse: {
      ...(initial?.byPointer.coarse ?? { acquisitions: 0, nearMisses: 0 }),
    },
    fine: {
      ...(initial?.byPointer.fine ?? { acquisitions: 0, nearMisses: 0 }),
    },
  };
  const byVerb: Record<ResizeVerb, number> = {
    drag: initial?.byVerb.drag ?? 0,
    preset: initial?.byVerb.preset ?? 0,
    numeric: initial?.byVerb.numeric ?? 0,
    keyboard: initial?.byVerb.keyboard ?? 0,
  };
  return {
    sink(event) {
      if (event.type === "acquire") {
        acquisitions++;
        byPointer[event.pointerKind].acquisitions++;
        if (event.nearMiss) {
          nearMisses++;
          byPointer[event.pointerKind].nearMisses++;
        }
      } else if (event.type === "resize") {
        byVerb[event.verb]++;
      } else regrets++;
    },
    summary: () => ({
      acquisitions,
      nearMisses,
      regrets,
      nearMissRate: acquisitions === 0 ? 0 : nearMisses / acquisitions,
      byPointer: {
        coarse: { ...byPointer.coarse },
        fine: { ...byPointer.fine },
      },
      byVerb: { ...byVerb },
    }),
  };
}

/** Current input kind. Read per call — a hybrid device switches mid-session. */
export function currentPointerKind(): PointerKind {
  if (typeof window === "undefined" || !window.matchMedia) return "fine";
  return window.matchMedia("(pointer: coarse)").matches ? "coarse" : "fine";
}

/** Where a persisted baseline lives. Versioned: a counter whose meaning
 *  changes must not be silently averaged with the old one. */
export const METRICS_STORAGE_KEY = "wb.resizeMetrics.v1";

export interface PersistentSink {
  readonly sink: MetricsSink;
  readonly summary: () => ResizeMetricsSummary;
  readonly reset: () => void;
}

/**
 * A counting sink whose totals survive a reload.
 *
 * Deliberately LOCAL-ONLY. The default sink discards everything on the
 * principle that measurement must never be a side effect the user did not ask
 * for; writing to the user's own localStorage keeps that promise — nothing
 * leaves the device, and there is still no backend to send to. It exists so a
 * baseline can accumulate across sessions instead of dying on every reload.
 *
 * Storage failures are swallowed: a private-mode quota error must never take
 * the editor down for the sake of a counter.
 */
export function createPersistentSink(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
  key: string = METRICS_STORAGE_KEY,
): PersistentSink {
  let restored: ResizeMetricsSummary | undefined;
  try {
    const raw = storage.getItem(key);
    if (raw) restored = JSON.parse(raw) as ResizeMetricsSummary;
  } catch {
    restored = undefined; // corrupt or unreadable → start clean, never throw
  }
  let counting = createCountingSink(restored);
  const save = (): void => {
    try {
      storage.setItem(key, JSON.stringify(counting.summary()));
    } catch {
      /* quota / private mode — counting continues in memory */
    }
  };
  return {
    sink(event) {
      counting.sink(event);
      save();
    },
    summary: () => counting.summary(),
    reset() {
      counting = createCountingSink();
      try {
        storage.removeItem(key);
      } catch {
        /* nothing to undo if it fails */
      }
    },
  };
}
